import { ApiError, api } from './api'
import type {
  PaginationMeta,
  RequestAttachment,
  ServiceRequest,
  ServiceRequestStatus,
  ServiceRequestType,
  ServiceRequestWithComments,
} from '@riverpark/types'

const API_BASE = import.meta.env.VITE_API_URL?.replace(/\/$/, '') ?? '/api'

export interface ListRequestsFilters {
  page?: number
  limit?: number
  status?: ServiceRequestStatus
  type?: ServiceRequestType
  /** Forces "my own requests" regardless of role — same override used across billing/payments. */
  mine?: boolean
}

export async function listServiceRequests(
  filters: ListRequestsFilters = {},
): Promise<{ data: ServiceRequest[]; meta?: PaginationMeta }> {
  const params = new URLSearchParams()
  if (filters.page) params.set('page', String(filters.page))
  if (filters.limit) params.set('limit', String(filters.limit))
  if (filters.status) params.set('status', filters.status)
  if (filters.type) params.set('type', filters.type)
  if (filters.mine) params.set('mine', 'true')
  const query = params.toString()
  return api.get<{ data: ServiceRequest[]; meta?: PaginationMeta }>(
    `/requests${query ? `?${query}` : ''}`,
  )
}

export async function getServiceRequest(id: string): Promise<{ data: ServiceRequestWithComments }> {
  return api.get<{ data: ServiceRequestWithComments }>(`/requests/${id}`)
}

export interface CreateServiceRequestInput {
  type: ServiceRequestType
  subject: string
  body: string
}

export async function createServiceRequest(
  input: CreateServiceRequestInput,
): Promise<{ data: { id: string } }> {
  return api.post<{ data: { id: string } }>('/requests', input)
}

export interface CreatePublicServiceRequestInput {
  type: ServiceRequestType
  subject: string
  body: string
  plotNumber?: string
  reporterName?: string
  reporterContact?: string
}

/** Unauthenticated — the QR-code / print-poster flow. No session required or sent. */
export async function createPublicServiceRequest(
  input: CreatePublicServiceRequestInput,
): Promise<{ data: { id: string } }> {
  return api.post<{ data: { id: string } }>('/requests/public', input)
}

export async function addRequestComment(
  requestId: string,
  body: string,
): Promise<{ data: { id: string } }> {
  return api.post<{ data: { id: string } }>(`/requests/${requestId}/comments`, { body })
}

export async function updateRequestStatus(
  requestId: string,
  status: ServiceRequestStatus,
): Promise<{ data: { success: boolean } }> {
  return api.patch<{ data: { success: boolean } }>(`/requests/${requestId}/status`, { status })
}

export async function uploadRequestAttachment(
  requestId: string,
  file: File,
): Promise<{ data: RequestAttachment }> {
  const form = new FormData()
  form.append('file', file)
  return api.postForm<{ data: RequestAttachment }>(`/requests/${requestId}/attachments`, form)
}

/** Unauthenticated — the public report flow uploads photos onto the request it just created,
 * before the reporter ever has a session. */
export async function uploadPublicRequestAttachment(
  requestId: string,
  file: File,
): Promise<{ data: RequestAttachment }> {
  const form = new FormData()
  form.append('file', file)
  return api.postForm<{ data: RequestAttachment }>(
    `/requests/public/${requestId}/attachments`,
    form,
  )
}

/** Cross-subdomain auth-cookie constraint, same as documents.ts's viewDocument/downloadDocument —
 * a plain <img src> or <a href> would drop the session cookie, so every use (thumbnail preview,
 * full-size open) goes through this fetch-and-blob path instead. Caller owns the object URL and
 * must revoke it when done. */
export async function fetchRequestAttachmentBlobUrl(
  requestId: string,
  attachmentId: string,
): Promise<string> {
  const res = await fetch(`${API_BASE}/requests/${requestId}/attachments/${attachmentId}`, {
    credentials: 'include',
  })
  if (!res.ok) throw new ApiError(res.status, 'Failed to load photo')
  const blob = await res.blob()
  return URL.createObjectURL(blob)
}
